import { useAction, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useState, useEffect } from "react";

export function usePrimoUpSetup() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupResult, setSetupResult] = useState<any>(null);

  const setupConnection = useAction(api.primoup.setupConnection);
  const checkEnvironment = useAction(api.primoup.checkEnvironmentVariables);
  const testConnection = useAction(api.primoup.testConnection);

  const setup = async () => {
    setIsLoading(true);
    setError(null);
    
    try {

      // Prima verifica le variabili d'ambiente
      const envCheck = await checkEnvironment();

      if (!envCheck.hasEmail || !envCheck.hasPassword || !envCheck.hasBaseUrl) {
        throw new Error("Missing PrimoUp environment variables. Please configure PRIMOUP_EMAIL, PRIMOUP_PASSWORD, and PRIMOUP_API_BASE_URL in Convex dashboard.");
      }
      
      // Poi prova il setup della connessione
      const result = await setupConnection();

      setSetupResult(result);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result;
    } catch (err: any) {
      console.error("❌ Setup failed:", err);
      setError(err.message || "Setup failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const test = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await testConnection();

      if (!result.success) {
        throw new Error(result.message);
      }
      
      return result;
    } catch (err: any) {
      console.error("❌ Test failed:", err);
      setError(err.message || "Test failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    setup,
    test,
    isLoading,
    error,
    setupResult,
  };
}

export function usePrimoUpConnection() {
  const connectionStatus = useQuery(api.primoup.getConnectionStatus, {});
  const callAPI = useAction(api.primoup.callPrimoUpAPI);
  const getUserByEmail = useAction(api.primoup.getUserByEmail);
  const syncWithPrimoUp = useAction(api.primoup.syncWithPrimoUp);
  const getLaboratoryServices = useAction(api.primoup.getLaboratoryServices);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const makeAPICall = async (endpoint: string, method = "GET", data?: any) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await callAPI({ endpoint, method, data });
      return result;
    } catch (err: any) {
      console.error("❌ API call failed:", err);
      setError(err.message || "API call failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getUser = async (email: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getUserByEmail({ email });
      return result;
    } catch (err: any) {
      console.error("❌ Get user failed:", err);
      setError(err.message || "Get user failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const syncUser = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await syncWithPrimoUp();
      return result;
    } catch (err: any) {
      console.error("❌ Sync failed:", err);
      setError(err.message || "Sync failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLaboratoryServices = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getLaboratoryServices();
      return result;
    } catch (err: any) {
      console.error("❌ Fetch laboratory services failed:", err);
      setError(err.message || "Fetch laboratory services failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    connectionStatus,
    makeAPICall,
    getUser,
    syncUser,
    fetchLaboratoryServices,
    isLoading,
    error,
  };
}

// Hook specializzato per i servizi di laboratorio
export function useLaboratoryServices() {
  const [services, setServices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number | null>(null);

  const getLaboratoryServices = useAction(api.primoup.getLaboratoryServices);

  const fetchServices = async (forceRefresh = false) => {
    // Cache per 5 minuti
    const CACHE_DURATION = 5 * 60 * 1000;
    const now = Date.now();
    
    if (!forceRefresh && lastFetch && (now - lastFetch) < CACHE_DURATION && services.length > 0) {
      return services;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getLaboratoryServices();
      
      setServices(result);
      setLastFetch(now);
      
      return result;
    } catch (err: any) {
      console.error("❌ Failed to fetch laboratory services:", err);
      setError(err.message || "Failed to fetch laboratory services");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-fetch on mount
  useEffect(() => {
    // The error is already exposed through state; effects have no caller to catch it.
    void fetchServices().catch(() => {});
  }, []);

  const refreshServices = () => fetchServices(true);

  const getServiceById = (id: number) => {
    return services.find(service => service.id === id);
  };

  const getServiceByName = (name: string) => {
    return services.find(service => 
      service.name?.toLowerCase() === name.toLowerCase()
    );
  };

  const searchServices = (query: string) => {
    if (!query.trim()) return services;
    
    const lowerQuery = query.toLowerCase();
    return services.filter(service => 
      service.name?.toLowerCase().includes(lowerQuery) ||
      service.description?.toLowerCase().includes(lowerQuery) ||
      service.code?.toLowerCase().includes(lowerQuery)
    );
  };

  return {
    services,
    isLoading,
    error,
    fetchServices,
    refreshServices,
    getServiceById,
    getServiceByName,
    searchServices,
    lastFetch,
  };
}

// Hook specializzato per i servizi di laboratorio dettagliati (con materials e tests)
export function useLaboratoryServicesDetailed() {
  const [services, setServices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number | null>(null);

  const getLaboratoryServicesDetailed = useAction(api.primoup.getLaboratoryServicesDetailed);

  const fetchServices = async (forceRefresh = false) => {
    // Cache per 5 minuti
    const CACHE_DURATION = 5 * 60 * 1000;
    const now = Date.now();
    
    if (!forceRefresh && lastFetch && (now - lastFetch) < CACHE_DURATION && services.length > 0) {
      return services;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      console.log("🔄 Fetching detailed laboratory services from PrimoUp...");
      const result = await getLaboratoryServicesDetailed();
      
      setServices(result || []);
      setLastFetch(now);
      console.log(`✅ Fetched ${result?.length || 0} detailed laboratory services`);
      
      return result || [];
    } catch (err: any) {
      console.error("❌ Failed to fetch detailed laboratory services:", err);
      setError(err.message || "Failed to fetch detailed services");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-fetch on mount
  useEffect(() => {
    // The error is already exposed through state; effects have no caller to catch it.
    void fetchServices().catch(() => {});
  }, []);

  const refreshServices = () => fetchServices(true);

  const getServiceById = (id: number) => {
    return services.find(service => service.id === id);
  };

  const getServiceByCode = (code: string) => {
    return services.find(service => 
      service.code?.toLowerCase() === code.toLowerCase()
    );
  };

  const searchServices = (query: string) => {
    if (!query.trim()) return services;
    
    const lowerQuery = query.toLowerCase();
    return services.filter(service => 
      service.name?.toLowerCase().includes(lowerQuery) ||
      service.code?.toLowerCase().includes(lowerQuery) ||
      service.slug?.toLowerCase().includes(lowerQuery)
    );
  };

  return {
    services,
    isLoading,
    error,
    fetchServices,
    refreshServices,
    getServiceById,
    getServiceByCode,
    searchServices,
    lastFetch,
  };
}
